import React, { useState } from 'react';
import { toast } from 'sonner';
import { Send } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { useLang } from '../context/LanguageContext';
import { submitLead } from '../lib/api';

const EMPTY = { name: '', telegram: '', email: '', phone: '', experience: '', message: '' };

export default function LeadModal({ open, onOpenChange, source = 'general', onSuccess, waitlist = false }) {
  const { t, lang } = useLang();
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await submitLead({ ...form, source, language: lang });
      if (waitlist) {
        toast.success(t('form.waitlistSuccessTitle'), { description: t('form.waitlistSuccessText') });
      } else {
        toast.success(t('form.successTitle'), { description: t('form.successText') });
      }
      setForm(EMPTY);
      onOpenChange(false);
      if (onSuccess) onSuccess();
    } catch (err) {
      toast.error(t('form.error'));
    } finally {
      setSending(false);
    }
  };

  const inputCls = 'w-full rounded-lg bg-[#0F172A] border border-slate-700 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 outline-none focus:border-blue-500/70 focus:ring-2 focus:ring-blue-500/20 transition-colors';
  const labelCls = 'block text-[11px] font-semibold tracking-widest text-slate-400 mb-1.5';
  const expOptions = t('form.expOptions');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#1E293B] border border-slate-700 shadow-2xl text-slate-100 sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="lead-modal">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-white" data-testid="lead-modal-title">{waitlist ? t('form.waitlistTitle') : t('form.title')}</DialogTitle>
          <DialogDescription className="text-slate-400 text-sm" data-testid="lead-modal-sub">{waitlist ? t('form.waitlistSub') : 'ALEF TRADERS'}</DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4 mt-2">
          <div>
            <label className={labelCls}>{t('form.name')} *</label>
            <input data-testid="lead-name-input" className={inputCls} placeholder={t('form.namePh')} value={form.name} onChange={set('name')} required />
          </div>
          <div>
            <label className={labelCls}>{t('form.telegram')} *</label>
            <input data-testid="lead-telegram-input" className={inputCls} placeholder={t('form.telegramPh')} value={form.telegram} onChange={set('telegram')} required />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls}>{t('form.email')} *</label>
              <input data-testid="lead-email-input" type="email" className={inputCls} placeholder={t('form.emailPh')} value={form.email} onChange={set('email')} required />
            </div>
            <div>
              <label className={labelCls}>{t('form.phone')} *</label>
              <input data-testid="lead-phone-input" type="tel" className={inputCls} placeholder={t('form.phonePh')} value={form.phone} onChange={set('phone')} required />
            </div>
          </div>
          <div>
            <label className={labelCls}>{t('form.experience')} *</label>
            <select data-testid="lead-experience-select" className={inputCls} value={form.experience} onChange={set('experience')} required>
              <option value="" disabled>—</option>
              {expOptions.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>{t('form.message')}</label>
            <textarea data-testid="lead-message-input" className={`${inputCls} min-h-[70px] resize-none`} placeholder={t('form.messagePh')} value={form.message} onChange={set('message')} />
          </div>
          <button
            data-testid="lead-submit-btn"
            type="submit"
            disabled={sending}
            className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-blue-500 px-6 py-3.5 text-base font-semibold text-white transition-all duration-300 hover:bg-blue-400 hover:shadow-[0_0_30px_rgba(59,130,246,0.45)] active:scale-[0.98] disabled:opacity-60"
          >
            <Send className="w-4 h-4" />
            {sending ? t('form.sending') : t('form.submit')}
          </button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
